import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { X } from '@untitledui/icons';
import { AlertCircle } from '@untitledui/icons/AlertCircle';
import { ChevronLeft } from '@untitledui/icons/ChevronLeft';
import { LinkExternal01 } from '@untitledui/icons/LinkExternal01';
import { ShieldTick } from '@untitledui/icons/ShieldTick';
import { includes } from 'lodash-es';
import { useEffect, useRef, useState } from 'react';

import {
  ProjectDisqualificationReason,
  PrequalificationResult,
  ScreenerResponseStatus,
} from '@/api/core-api/types.gen';
import { KindOfResearch } from '@/api/enums';
import { Button, LinkButton } from '@/components/base/buttons/button';
import { LoadingIndicator } from '@/components/base/loading-indicator/loading-indicator';
import { notify } from '@/components/base/notifications/notifications';
import { queryKeys } from '@/hooks/api/query-keys';
import { useLinkedinVolOAuthReturn } from '@/hooks/use-linkedin-vol-oauth-return';
import { usePersistProjectReferralFromSearch } from '@/hooks/use-persist-project-referral-from-search';
import { useStartExternalScreener } from '@/hooks/use-start-external-screener';
import { useAnalytics } from '@/lib/analytics/use-analytics';
import { formatDateTime } from '@/utils/date';
import { screenerSubmittedDedupId } from '@common/utils';

import type { ProjectSearchParams } from './-shared';

import { EmptyActiveProjectsIcon } from '../-components/empty-active-projects-icon';
import { AlreadyAppliedMessage } from './-components/already-applied-message';
import { PreInviteScreen } from './-components/pre-invite-screen';
import { PreQualificationForm } from './-components/pre-qualification-form';
import { PreSubmissionSteps } from './-components/pre-submission-steps';
import { ProjectSnapshot } from './-components/project-snapshot';
import { ScreenerForm } from './-components/screener-form';
import { ScreenerInfoBox } from './-components/screener-info-box';
import { ScreenerOutcomeScreen } from './-components/screener-outcome-screen';
import { SkipScreenerApply } from './-components/skip-screener-apply';
import { useScreenerSubmission } from './-hooks/use-screener-submission';
import {
  projectSearchSchema,
  prefetchProjectData,
  prefetchScreenerData,
  projectQueryOptions,
  screenerQueryOptions,
} from './-shared';

export const Route = createFileRoute(
  '/participants/_authenticated/projects/$projectId/apply',
)({
  validateSearch: projectSearchSchema,

  beforeLoad: async ({ context, params, search }) => {
    const { queryClient, user } = context;
    const userId = user.id;

    // Step 1: Prefetch project data (handles redirects for incomplete profile, etc.)
    await prefetchProjectData(queryClient, params.projectId, search, userId);

    // Step 2: Check if we should prefetch screener
    const projectResult = await queryClient.ensureQueryData(
      projectQueryOptions(params.projectId, search, userId),
    );

    if (projectResult.success && projectResult.response?.project) {
      const { screenerResponseStatus } = projectResult.response;
      const alreadyResponded = !!screenerResponseStatus;

      const disqualified =
        projectResult.response.project.prequalifies?.result ===
        PrequalificationResult.DISQUALIFIED;

      // Only prefetch screener if user qualifies and hasn't already applied
      if (!disqualified && !alreadyResponded) {
        await prefetchScreenerData(queryClient, params.projectId, userId, {
          inviteCode: search?.inviteCode ?? search?.invite,
          inviteId: search?.inviteId,
        });
      }
    }
  },

  component: ApplyPageWrapper,
});

function ApplyPageWrapper() {
  const { projectId } = Route.useParams();
  const search = Route.useSearch();
  const { user } = Route.useRouteContext();
  useLinkedinVolOAuthReturn(user?.id);
  return <ApplyPage projectId={projectId} search={search} />;
}

interface ApplyPageProps {
  projectId: string;
  search?: ProjectSearchParams;
}

function ApplyPage({ projectId, search }: ApplyPageProps) {
  const queryClient = useQueryClient();
  const analytics = useAnalytics();
  const { profileId, user } = Route.useRouteContext();
  const userId = user.id;

  const [failedPreQualificationCheck, setFailedPreQualificationCheck] =
    useState(false);

  const [preQualPassed, setPreQualPassed] = useState(false);

  const [screenerError, setScreenerError] = useState<'stale-data' | null>(null);

  const { data: projectResult } = useQuery(
    projectQueryOptions(projectId, search, userId),
  );

  const { data: screenerResult, isLoading: isLoadingScreener } = useQuery({
    ...screenerQueryOptions(projectId, userId, {
      inviteCode: search?.inviteCode ?? search?.invite,
      inviteId: search?.inviteId,
    }),
    // While the response sits in the partner's screener the partner may post
    // the answers at any moment (including right after redirecting the
    // participant back here) — poll so the page flips to the applied view on
    // its own once the reducer promotes the response.
    // Only while the response is actually with the partner: IN_PROGRESS
    // with an outstanding step means the partner's answers have already
    // landed and the participant is on a verification step here.
    refetchInterval: (query) => {
      const data = query.state.data;
      if (!data?.success) return false;
      const existing = data.data.existingResponse;
      return existing?.status ===
        ScreenerResponseStatus.EXTERNAL_SCREENER_IN_PROGRESS &&
        !hasOutstandingStep(existing.outstandingSteps)
        ? 5000
        : false;
    },
  });

  const hasPreSubmissionSteps =
    !!screenerResult?.success &&
    hasOutstandingStep(screenerResult.data.existingResponse?.outstandingSteps);

  usePersistProjectReferralFromSearch(projectId, search);

  // The invitation is what qualifies a skip-the-screener applicant, so the
  // submission must name it. The emailed link carries `inviteId`; a
  // participant arriving without it (dashboard, retyped URL) falls back to
  // the invitation the server matched to their profile on the project view.
  const inviteId =
    search?.inviteId ??
    (projectResult?.success ? projectResult.response?.inviteId : undefined) ??
    undefined;

  const { saveResponses, submitWithoutQuestions, isSubmitting, error } =
    useScreenerSubmission({
      projectId,
      inviteId,
      inviteCode: search?.inviteCode ?? search?.invite,
    });

  const navigate = useNavigate();

  // AI-mod auto-invite: when an AI-moderated participant lands here with an
  // INVITED screener response (either freshly auto-invited after submit or
  // returning after navigating away), send them straight into the interview.
  const screenerStatus = screenerResult?.success
    ? screenerResult.data.existingResponse?.status
    : undefined;

  const isAiModProject = projectResult?.success
    ? projectResult.response?.project?.kindOfResearch ===
      KindOfResearch.AI_MODERATED_INTERVIEW
    : undefined;

  const isInvited = screenerStatus === ScreenerResponseStatus.INVITED;

  // Derived here rather than from `projectData` (defined below) because the
  // redirect effect needs it: a finished study must not push an invitee into
  // the interview, it must fall through to ClosedMessage (PX-410).
  const isStudyOver = projectResult?.success
    ? projectResult.response?.isStudyOver === true
    : false;

  useEffect(() => {
    if (!isInvited || !isAiModProject || isStudyOver) return;
    void navigate({
      to: '/participants/projects/$projectId/interview',
      params: { projectId },
    });
  }, [isInvited, navigate, projectId, isAiModProject, isStudyOver]);

  if (isLoadingScreener) {
    return (
      <PageContainer>
        <div className="flex items-center justify-center py-16">
          <LoadingIndicator />
        </div>
      </PageContainer>
    );
  }

  // Handle project data loading/errors
  if (!projectResult?.success || !projectResult.response) {
    return (
      <PageContainer>
        <ErrorMessage message="Failed to load project" />
      </PageContainer>
    );
  }

  const projectData = projectResult.response;
  const project = projectData.project;

  if (!project) {
    return (
      <PageContainer>
        <ErrorMessage message="Project not found" />
      </PageContainer>
    );
  }

  const isInvitedApplicant = !!(
    inviteId ||
    search?.inviteCode ||
    search?.invite
  );

  // Shared by the screener form and the skip-the-screener apply step so the
  // two ways of filing an application report identically.
  const trackApplied = (filedStatus: ScreenerResponseStatus) => {
    analytics?.track({
      action: 'applied',
      actionSubject: 'project',
      attributes: {
        projectId,
        projectTitle: project.name,
        rewardUsd: project.respondentRemuneration,
        isInvited: isInvitedApplicant,
      },
    });
    // The launch conversion, and only when the save that finished the
    // screener actually filed the application. A response still held on
    // an outstanding verification step comes back DRAFT: the server
    // reports it from the transition that eventually releases it, and
    // emitting now would report a submission that has not happened.
    //
    // The id is derived from this project and this participant, so this
    // and the server's report of the same conversion collapse into one at
    // the platform however far apart they land.
    if (filedStatus !== ScreenerResponseStatus.DRAFT) {
      analytics?.conversion({
        name: 'screener_submitted',
        eventId: screenerSubmittedDedupId(projectId, userId),
        attributes: {
          projectId,
          rewardUsd: project.respondentRemuneration,
          isInvited: isInvitedApplicant,
        },
      });
    }
  };

  const alreadyResponded =
    !!screenerResult?.success &&
    !!screenerResult.data.existingResponse?.status &&
    !includes(
      [
        ScreenerResponseStatus.DRAFT,
        ScreenerResponseStatus.EXTERNAL_SCREENER_IN_PROGRESS,
      ],
      screenerResult.data.existingResponse?.status,
    );

  const isClosed =
    projectData.isProjectClosed || projectData.isClosedToNewApplications;

  // A finished study withdraws every onward route: the AI-mod redirect below,
  // and PreInviteScreen's "Start project" after it. Sits ahead of both, and is
  // narrower than `isClosed` on purpose — `isClosed` folds in
  // `isClosedToNewApplications`, which is true for a paused project, and a
  // paused study must still let an invitee through (PX-410).
  if (isStudyOver) {
    return (
      <PageContainer>
        <BackLink projectId={projectId} variant="back" />
        <ClosedMessage />
      </PageContainer>
    );
  }

  // AI-mod auto-invite redirects to /interview (handled by effect above);
  // render a loading state so the participant doesn't flash AlreadyApplied.
  if (isInvited && isAiModProject) {
    return (
      <PageContainer>
        <div className="flex items-center justify-center py-16">
          <LoadingIndicator />
        </div>
      </PageContainer>
    );
  }

  // Auto-invited on a non-AI-mod project: celebrate and send the participant
  // straight into the study (or to booking, for moderated studies).
  if (isInvited) {
    return (
      <PageContainer>
        <BackLink projectId={projectId} variant="back" />
        <PreInviteScreen
          projectId={projectId}
          userId={userId}
          project={project}
          projectData={projectData}
        />
      </PageContainer>
    );
  }

  const existingResponse = screenerResult?.success
    ? screenerResult.data.existingResponse
    : undefined;

  // Handle successful submission or already applied
  if (alreadyResponded) {
    return (
      <PageContainer>
        <BackLink projectId={projectId} variant="back" />
        <ScreenerOutcomeScreen
          outcome={existingResponse?.outcome}
          idVerificationPending={existingResponse?.idVerificationPending}
          projectId={projectId}
          userId={userId}
          profileId={profileId}
        />
      </PageContainer>
    );
  }

  if (isClosed) {
    return (
      <PageContainer>
        <BackLink projectId={projectId} variant="back" />
        <ClosedMessage />
      </PageContainer>
    );
  }

  // Follow-up invite with "skip the screener": the researcher already knows
  // this participant from an earlier study, so they are not re-screened.
  // Sits ahead of prequalification, the screener-limit gate and the
  // questions themselves — an invitee is exempt from all three. The server
  // qualifies the submission from the invitation regardless of answers, so
  // none are sent; only the project agreement, if any, still needs a decision.
  if (screenerResult?.success && screenerResult.data.skipScreenerQuestions) {
    return (
      <PageContainer>
        <BackLink projectId={projectId} variant="back" />
        <SkipScreenerApply
          project={project}
          nda={screenerResult.data.nda}
          isSubmitting={isSubmitting}
          error={error}
          onApply={async (agree) => {
            // `agree: false` on a project with no agreement is a no-op for the
            // server; it is passed so the empty save is not short-circuited
            // client-side as an answers-less non-submission.
            // Failures are already surfaced by the submission hook's toast.
            const filed = await saveResponses([], [], agree ?? false).catch(
              () => null,
            );
            if (!filed) return;
            trackApplied(filed.status);
            void queryClient.invalidateQueries({
              queryKey: queryKeys.projects.screener(projectId, userId),
            });
          }}
        />
      </PageContainer>
    );
  }

  if (
    projectData.screenerCycle &&
    projectData.screenerCycle.screenerCount >=
      projectData.screenerCycle.limit &&
    // `isMatched` covers an API that predates the field (independent deploys).
    !(projectData.isExemptFromScreenerLimit ?? projectData.isMatched)
  ) {
    return (
      <PageContainer>
        <BackLink projectId={projectId} variant="back" />
        <ScreenerLimitReachedMessage
          cycleExpiryDate={projectData.screenerCycle.cycleExpiryDate}
        />
      </PageContainer>
    );
  }

  if (failedPreQualificationCheck) {
    return (
      <PageContainer>
        <BackLink projectId={projectId} variant="back" />
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-[375px_1fr]">
          <div className="self-start">
            <ProjectSnapshot project={project} />
          </div>
          <FailedPreQualificationMessage />
        </div>
      </PageContainer>
    );
  }

  // Handle disqualification
  const disqualified =
    project.prequalifies?.result === PrequalificationResult.DISQUALIFIED;

  if (disqualified) {
    return (
      <PageContainer>
        <BackLink projectId={projectId} variant="back" />
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-[375px_1fr]">
          <div className="self-start">
            <ProjectSnapshot project={project} />
          </div>
          <DisqualifiedMessage
            reason={project.prequalifies?.disqualificationReason}
          />
        </div>
      </PageContainer>
    );
  }

  // Phase 1: Pre-qualification questions (rendered regardless of screener load state)
  const preQualQuestions =
    projectData.project?.prequalifies?.requiredProfileCompletionQuestions ?? [];

  const hasPreQualQuestions = preQualQuestions.length > 0;

  const screenerQuestionCount = screenerResult?.success
    ? Math.max(0, screenerResult.data.questions?.length ?? 0)
    : 0;

  if (hasPreQualQuestions && !preQualPassed) {
    const totalQuestions = preQualQuestions.length + screenerQuestionCount;

    return (
      <PageContainer>
        <BackLink projectId={projectId} />
        <PreQualificationForm
          projectId={projectId}
          userId={userId}
          profileId={profileId}
          project={project}
          preQualificationQuestions={preQualQuestions}
          totalQuestions={totalQuestions}
          // An invitation waives the targeting, so the server only asked for
          // the quota's attributes; a requalify here could wrongly disqualify.
          skipQualificationCheck={Boolean(
            projectData.inviteId || projectData.inviteCode,
          )}
          onPassedCheck={() => setPreQualPassed(true)}
          onFailedCheck={() => setFailedPreQualificationCheck(true)}
        />
      </PageContainer>
    );
  }

  // Handle screener data loading/errors
  if (!screenerResult?.success) {
    return (
      <PageContainer>
        <BackLink projectId={projectId} />
        <ErrorMessage message="Failed to load screener questions" />
      </PageContainer>
    );
  }

  const screenerData = screenerResult.data;

  // Pre-submission steps (NDA / LinkedIn / phone / Plaid). Reached on DRAFT
  // classically, and on EXTERNAL_SCREENER_IN_PROGRESS once the partner's
  // answers have landed: the reducer defers every step while the response is
  // with the partner, so an IN_PROGRESS row with any step outstanding is back
  // from the partner and finishing profile completion here — not a reason to
  // show the handoff again. This branch must stay above the handoff.
  if (
    screenerData.existingResponse &&
    (screenerData.existingResponse.status === ScreenerResponseStatus.DRAFT ||
      screenerData.existingResponse.status ===
        ScreenerResponseStatus.EXTERNAL_SCREENER_IN_PROGRESS) &&
    screenerData.existingResponse.outstandingSteps &&
    hasPreSubmissionSteps &&
    screenerData.isScreenerComplete
  ) {
    return (
      <PageContainer>
        <BackLink projectId={projectId} />
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-[375px_1fr]">
          {/* Left: Project Snapshot + Info */}
          <div className="hidden flex-col gap-4 self-start lg:flex">
            <ProjectSnapshot project={project} />
            <ScreenerInfoBox />
          </div>
          <PreSubmissionSteps
            projectId={projectId}
            userId={userId}
            profileId={profileId}
            nda={screenerData.nda}
            saveNdaResult={async (hasAccepted: boolean) => {
              await saveResponses([], [], hasAccepted);
            }}
            isSubmitting={isSubmitting}
            error={error}
            preSubmissionSteps={screenerData.existingResponse?.outstandingSteps}
          />
        </div>
      </PageContainer>
    );
  }

  // External-screener handoff. Two ways in:
  //  - the response is with the partner's screener: IN_PROGRESS with no step
  //    outstanding (the steps branch above caught the other case), whether
  //    straight after the completing save, once the invalidated screener
  //    query refetches, or on any later visit;
  //  - no response exists yet and the project has no internal questions, so
  //    the partner's screener is the whole screener. "Complete my screener"
  //    then creates the response server-side and returns the link.
  const isWithPartner =
    screenerData.existingResponse?.status ===
    ScreenerResponseStatus.EXTERNAL_SCREENER_IN_PROGRESS;

  const internalQuestionsLength = screenerData.questions?.length ?? 0;

  const isExternalOnlyFirstVisit =
    !screenerData.existingResponse &&
    (projectData.externalQuestionsLength ?? 0) > 0 &&
    internalQuestionsLength === 0;

  if (isWithPartner || isExternalOnlyFirstVisit) {
    return (
      <PageContainer>
        <BackLink projectId={projectId} variant="back" />
        <ExternalScreenerHandoffMessage
          projectId={projectId}
          userId={userId}
          inviteId={inviteId}
          inviteCode={search?.inviteCode ?? search?.invite}
          externalScreenerLink={
            screenerData.existingResponse?.externalScreenerLink
          }
          internalQuestionsLength={internalQuestionsLength}
          companyName={
            project.hideCompanyName ? undefined : project.companyName?.trim()
          }
        />
      </PageContainer>
    );
  }

  // A project with no screener questions (PX-453: its organization is on the
  // allow-zero-screener-questions flag, or the server would have refused to
  // serve the screener). There is nothing to ask, so the application is filed
  // on arrival: the same empty save a completed screener sends, after which
  // the invalidated screener query returns the response and the outcome
  // screen above takes over. Reaching here means no application has been
  // filed yet — a filed response returned above as `alreadyResponded`, one
  // with the partner as `isWithPartner`, and a DRAFT on a verification step
  // as pre-submission steps — so a DRAFT left over from questions that have
  // since been removed is filed the same way as a first visit.
  if (!screenerData.questions || screenerData.questions.length === 0) {
    return (
      <PageContainer>
        <BackLink projectId={projectId} variant="back" />
        <NoScreenerAutoApply
          isSubmitting={isSubmitting}
          error={error}
          onSubmit={async () => {
            // Failures are already surfaced by the submission hook's toast.
            const filed = await submitWithoutQuestions().catch(() => null);
            if (!filed) return;
            trackApplied(filed.status);
            void queryClient.invalidateQueries({
              queryKey: queryKeys.projects.screener(projectId, userId),
            });
          }}
        />
      </PageContainer>
    );
  }

  // Screener has no remaining navigable questions. Under normal flow,
  // status would already have transitioned to PENDING and been caught by
  // `alreadyResponded` above. Reaching here means either a stuck DRAFT
  // (server didn't transition after the last save) or a project with no
  // screener questions and no outstanding steps. Either way, there's
  // nothing for the participant to do — treat as already applied rather
  // than rendering an empty screener form.
  //
  // The `nextQuestionUid === null` branch is redundant with
  // `isScreenerComplete` per the server contract; it's kept here to
  // narrow `nextQuestionUid` from `string | null` to `string` for the
  // `ScreenerForm` prop below.
  if (
    screenerData.isScreenerComplete ||
    screenerData.nextQuestionUid === null
  ) {
    return (
      <PageContainer>
        <BackLink projectId={projectId} variant="back" />
        <AlreadyAppliedMessage
          projectId={projectId}
          profileId={profileId}
          idVerificationPending={existingResponse?.idVerificationPending}
        />
      </PageContainer>
    );
  }

  // Form signalled an unrecoverable state (e.g. the server returned a
  // `nextQuestionUid` that isn't in our cached `questions` because a
  // researcher edited the screener mid-flow). Render a refresh-prompt
  // rather than trying to silently recover.
  if (screenerError === 'stale-data') {
    return (
      <PageContainer>
        <BackLink projectId={projectId} variant="back" />
        <ScreenerBadDataMessage
          onRefresh={() => globalThis.location.reload()}
        />
      </PageContainer>
    );
  }

  // Phase 2: Screener questions
  return (
    <PageContainer>
      <BackLink projectId={projectId} />
      <ScreenerForm
        userId={userId}
        profileId={profileId}
        projectId={projectId}
        project={project}
        questions={screenerData.questions}
        questionOffset={preQualQuestions.length}
        totalQuestions={preQualQuestions.length + screenerQuestionCount}
        existingResponse={screenerData.existingResponse}
        initialNextQuestionUid={screenerData.nextQuestionUid}
        initialIsNextQuestionLast={screenerData.isNextQuestionLast}
        inviteId={search?.inviteId}
        inviteCode={search?.inviteCode ?? search?.invite}
        onSubmitSuccess={(filed) => {
          trackApplied(filed.status);
          void queryClient.invalidateQueries({
            queryKey: queryKeys.projects.screener(projectId, userId),
          });
        }}
        onError={setScreenerError}
      />
    </PageContainer>
  );
}

/** Whether the server holds the response on any pre-submission step. */
function hasOutstandingStep(
  steps:
    | { linkedin?: boolean; plaid?: boolean; phone?: boolean; nda?: boolean }
    | null
    | undefined,
): boolean {
  return !!(steps?.linkedin || steps?.plaid || steps?.phone || steps?.nda);
}

function PageContainer({ children }: { children: React.ReactNode }) {
  return (
    <div className="mx-auto max-w-[var(--max-width-container)] px-4 pt-6 pb-18 md:px-8">
      {children}
    </div>
  );
}

function BackLink({
  projectId,
  variant = 'quit',
}: {
  projectId: string;
  variant?: 'quit' | 'back';
}) {
  const navigate = useNavigate();

  if (variant === 'quit') {
    return (
      <Button
        type="button"
        color="link-color"
        onClick={() => {
          void navigate({
            to: '/participants/projects/$projectId/{-$slug}',
            params: { projectId },
          });
        }}
        iconLeading={ChevronLeft}
        size="md"
        className="mb-6 text-md font-semibold"
        data-testid="screener-quit-button"
      >
        <Trans>Quit screener question</Trans>
      </Button>
    );
  }

  return (
    <Link
      to="/participants/projects/$projectId/{-$slug}"
      params={{ projectId }}
      className="mb-6 inline-flex items-center gap-1.5 text-brand-secondary hover:text-brand-secondary_hover"
    >
      <ChevronLeft className="size-5" />
      <span className="text-md font-semibold">
        <Trans>Back to project</Trans>
      </span>
    </Link>
  );
}

function ErrorMessage({ message }: { message: string }) {
  return (
    <div className="py-16 text-center">
      <AlertCircle className="mx-auto mb-4 size-12 text-utility-error-500" />
      <h1 className="mb-2 font-heading text-xl font-semibold text-primary">
        <Trans>Something went wrong</Trans>
      </h1>
      <p className="mb-6 text-secondary">{message}</p>
      <Button
        color="secondary"
        onClick={() => {
          globalThis.location.reload();
        }}
      >
        <Trans>Try again</Trans>
      </Button>
    </div>
  );
}

function FailedPreQualificationMessage() {
  return (
    <div
      className="flex flex-col items-center justify-center gap-6 p-16"
      data-testid="prequalification-failed"
    >
      <EmptyActiveProjectsIcon />

      <p className="text-center text-md font-semibold text-primary">
        <Trans>
          Your answers didn&apos;t quite match the requirements for this study.
        </Trans>
      </p>
      <p className="text-center text-md font-semibold text-primary">
        <Trans>
          Every study is different! Most participants get selected after a few
          tries, so keep browsing to find one that fits your profile.
        </Trans>
      </p>
      <LinkButton color="primary" iconLeading={X} to="/participants/projects">
        <Trans>Go back to projects</Trans>
      </LinkButton>
    </div>
  );
}

function ScreenerBadDataMessage({ onRefresh }: { onRefresh: () => void }) {
  return (
    <div className="flex flex-col items-center py-8 text-center">
      <AlertCircle className="mb-4 size-12 text-utility-warning-500" />
      <h1 className="mb-2 font-heading text-xl font-semibold text-primary">
        <Trans>Sorry, we couldn&apos;t find the next question</Trans>
      </h1>
      <p className="mb-6 text-secondary">
        <Trans>Please refresh the page to continue.</Trans>
      </p>
      <Button color="primary" size="lg" onClick={onRefresh}>
        <Trans>Refresh</Trans>
      </Button>
    </div>
  );
}

/**
 * The apply step for a project with no screener questions (PX-453: its
 * organization is on the allow-zero-screener-questions flag). There is
 * nothing to answer, so the application is filed the moment the participant
 * lands here: the parent sends the same empty save a completed screener
 * would, and the outcome screen takes over once it is accepted. Only a
 * failed save leaves anything to act on.
 */
function NoScreenerAutoApply({
  onSubmit,
  isSubmitting,
  error,
}: {
  onSubmit: () => void;
  isSubmitting: boolean;
  error: Error | null;
}) {
  // One automatic attempt per visit, whatever re-renders the parent goes
  // through while the save is in flight; a retry is the participant's call.
  const hasSubmitted = useRef(false);

  useEffect(() => {
    if (hasSubmitted.current) return;
    hasSubmitted.current = true;
    onSubmit();
  }, [onSubmit]);

  if (error && !isSubmitting) {
    return (
      <div
        className="flex flex-col items-center py-8 text-center"
        data-testid="no-screener-auto-apply-failed"
      >
        <AlertCircle className="mb-4 size-12 text-utility-error-500" />
        <h1 className="mb-2 font-heading text-xl font-semibold text-primary">
          <Trans>We couldn&apos;t submit your application</Trans>
        </h1>
        <p className="mb-6 text-secondary">
          <Trans>Please try again.</Trans>
        </p>
        <Button color="primary" onClick={onSubmit}>
          <Trans>Try again</Trans>
        </Button>
      </div>
    );
  }

  return (
    <div
      className="flex flex-col items-center gap-4 py-16"
      data-testid="no-screener-auto-apply"
    >
      <LoadingIndicator />
      <p className="text-md text-secondary">
        <Trans>Submitting your application…</Trans>
      </p>
    </div>
  );
}

function ClosedMessage() {
  return (
    <div className="flex flex-col items-center py-8 text-center">
      <AlertCircle className="mb-4 size-12 text-utility-warning-500" />
      <h1 className="mb-2 font-heading text-xl font-semibold text-primary">
        <Trans>This project is not currently accepting new applications</Trans>
      </h1>
      <p className="mb-6 text-secondary">
        <Trans>Please check back later or browse other projects.</Trans>
      </p>
      <LinkButton color="primary" size="lg" to="/participants/projects">
        <Trans>Browse other projects</Trans>
      </LinkButton>
    </div>
  );
}

function ScreenerLimitReachedMessage({
  cycleExpiryDate,
}: {
  cycleExpiryDate?: string | null;
}) {
  return (
    <div className="flex flex-col items-center py-8 text-center">
      <AlertCircle className="mb-4 size-12 text-utility-warning-500" />
      <h1 className="mb-2 font-heading text-xl font-semibold text-primary">
        <Trans>
          You&apos;ve reached the daily limit for project applications
        </Trans>
      </h1>
      <p className="mb-6 text-secondary">
        {cycleExpiryDate ? (
          <Trans>
            You can apply again at {formatDateTime(cycleExpiryDate)}
          </Trans>
        ) : (
          <Trans>Please try again later</Trans>
        )}
      </p>
      <LinkButton color="primary" size="lg" to="/participants/projects">
        <Trans>Browse other projects</Trans>
      </LinkButton>
    </div>
  );
}

/** Hostname shown under the continue button so the handoff names its destination. */
function getScreenerHostname(url?: string | null): string | undefined {
  if (!url) return undefined;
  try {
    return new URL(url).hostname;
  } catch {
    return undefined;
  }
}

function ExternalScreenerHandoffStep({
  step,
  title,
  children,
}: {
  step: number;
  title: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <li className="flex items-start gap-4">
      <span className="flex size-7 shrink-0 items-center justify-center rounded-full bg-brand-primary text-sm font-semibold text-brand-secondary">
        {step}
      </span>
      <div className="flex flex-col gap-1">
        <h2 className="text-md font-semibold text-primary">{title}</h2>
        <p className="text-md text-secondary">{children}</p>
      </div>
    </li>
  );
}

function ExternalScreenerHandoffMessage({
  externalScreenerLink,
  internalQuestionsLength,
  companyName,
  projectId,
  userId,
  inviteId,
  inviteCode,
}: {
  externalScreenerLink?: string | null;
  internalQuestionsLength: number;
  /** Partner display name; absent when unset or the project hides it. */
  companyName?: string;
  projectId: string;
  userId: string;
  inviteId?: string | null;
  inviteCode?: string | null;
}) {
  const screenerHostname = getScreenerHostname(externalScreenerLink);

  const startExternalScreenerMutation = useStartExternalScreener();

  return (
    <div
      className="mx-auto flex max-w-3xl flex-col items-center pt-6"
      data-testid="external-screener-handoff"
    >
      <h1 className="mb-2 max-w-xl text-center font-heading text-display-xs font-semibold text-primary md:text-display-sm">
        {companyName ? (
          <Trans>
            Complete {internalQuestionsLength ? 'the rest of' : ''} your
            screener on {companyName}&apos;s platform
          </Trans>
        ) : (
          <Trans>
            Complete {internalQuestionsLength ? 'the rest of' : ''} your
            screener on the researcher&apos;s platform
          </Trans>
        )}
      </h1>
      {internalQuestionsLength > 0 && (
        <p className="mb-8 text-center text-lg text-secondary">
          <Trans>Your screener application is not finished yet</Trans>
        </p>
      )}

      <div className="mb-8 w-full rounded-xl border border-secondary p-6 md:p-8">
        <ol className="flex flex-col gap-6">
          <ExternalScreenerHandoffStep
            step={1}
            title={
              companyName ? (
                <Trans>
                  Complete your screener application on {companyName}&apos;s
                  platform
                </Trans>
              ) : (
                <Trans>
                  Complete your screener application on the researcher&apos;s
                  platform
                </Trans>
              )
            }
          >
            {companyName ? (
              <Trans>
                Upon clicking &lsquo;Complete my screener&rsquo; below, you will
                be redirected to {companyName}&apos;s platform to complete your
                screener application.
              </Trans>
            ) : (
              <Trans>
                Upon clicking &lsquo;Complete my screener&rsquo; below, you will
                be redirected to the researcher&apos;s platform to complete your
                screener application.
              </Trans>
            )}
          </ExternalScreenerHandoffStep>

          <ExternalScreenerHandoffStep
            step={2}
            title={<Trans>Come straight back</Trans>}
          >
            {companyName ? (
              <Trans>
                {companyName} returns you to this page the moment you submit the
                application. You may be asked to complete additional ID
                verification steps if you have not already.
              </Trans>
            ) : (
              <Trans>
                The researcher returns you to this page the moment you submit
                the application. You may be asked to complete additional ID
                verification steps if you have not already.
              </Trans>
            )}
          </ExternalScreenerHandoffStep>

          <ExternalScreenerHandoffStep
            step={3}
            title={
              <Trans>Your application is submitted to the researcher</Trans>
            }
          >
            <Trans>
              Your application on Respondent should show an outcome, and should
              be visible to the researcher to be handpicked.
            </Trans>
          </ExternalScreenerHandoffStep>
        </ol>
      </div>

      <div className="mb-8 flex w-full items-start gap-3">
        <ShieldTick className="mt-0.5 size-5 shrink-0 text-fg-quaternary" />
        <p className="text-md text-secondary">
          <span className="font-semibold text-primary">
            <Trans>
              You&apos;re leaving the Respondent platform, so it&apos;s worth
              knowing what&apos;s normal.
            </Trans>
          </span>{' '}
          {companyName ? (
            <Trans>{companyName} will only ask you screener questions.</Trans>
          ) : (
            <Trans>The researcher will only ask you screener questions.</Trans>
          )}{' '}
          <Trans>
            They should never ask for your Respondent password, a sign-in code,
            or any contact information like your phone number or email. If their
            site asks for any of it, don&apos;t enter it — close the tab and{' '}
            <a
              href="mailto:support@respondent.io"
              className="font-semibold text-brand-secondary underline hover:text-brand-secondary_hover"
            >
              report it to us
            </a>
            .
          </Trans>
        </p>
      </div>

      {/*
       * The continue action asks the server for the partner link rather than
       * navigating to the one on the response: when no response exists yet
       * (a project with no internal questions) the server creates it first,
       * in EXTERNAL_SCREENER_IN_PROGRESS, and builds the link off that row.
       */}
      <>
        <Button
          color="primary"
          size="lg"
          isLoading={startExternalScreenerMutation.isPending}
          onClick={() => {
            startExternalScreenerMutation.mutate(
              {
                projectId,
                userId,
                inviteId: inviteId ?? undefined,
                inviteCode: inviteCode ?? undefined,
              },
              {
                onSuccess: ({ externalScreenerLink }) => {
                  globalThis.location.assign(externalScreenerLink);
                },
                onError: (error) => {
                  notify({
                    title: t`Failed to start external screener`,
                    description: error.message,
                    color: 'error',
                  });
                },
              },
            );
          }}
        >
          <Trans>Complete my screener</Trans>
        </Button>
        {screenerHostname && (
          <p className="mt-3 flex items-center gap-1.5 text-md text-tertiary">
            <LinkExternal01 className="size-4" />
            <Trans>Opens {screenerHostname}</Trans>
          </p>
        )}
      </>
    </div>
  );
}

function DisqualifiedMessage({
  reason,
}: {
  reason?: ProjectDisqualificationReason;
}) {
  const getReasonMessage = () => {
    switch (reason) {
      case ProjectDisqualificationReason.MUST_COMPLETE_PROFILE:
        return (
          <Trans>Please complete your profile to apply for this project.</Trans>
        );
      case ProjectDisqualificationReason.WORK_EMAIL_REQUIRED:
        return (
          <Trans>This project requires a verified work email address.</Trans>
        );
      case ProjectDisqualificationReason.TRUSTED_COMPANY_CHECK_PENDING:
        return (
          <Trans>
            Your work email is verified. We&apos;re confirming your company
            details so you can apply to this project.
          </Trans>
        );
      case ProjectDisqualificationReason.INCORRECT_DEMOGRAPHICS:
        return (
          <Trans>
            Your profile doesn&apos;t match the requirements for this project.
          </Trans>
        );
      case ProjectDisqualificationReason.PAST_PARTICIPATION:
        return (
          <Trans>
            This researcher is looking for participants they haven&apos;t worked
            with recently, so you&apos;re not eligible this time.
          </Trans>
        );
      default:
        return (
          <Trans>
            Unfortunately, you don&apos;t qualify for this project based on the
            researcher&apos;s criteria.
          </Trans>
        );
    }
  };

  return (
    <div className="flex flex-col items-center py-8 text-center">
      <AlertCircle className="mb-4 size-12 text-utility-warning-500" />
      <h1 className="mb-2 font-heading text-xl font-semibold text-primary">
        <Trans>Not eligible</Trans>
      </h1>
      <p className="mb-6 text-secondary">{getReasonMessage()}</p>
      {reason === ProjectDisqualificationReason.MUST_COMPLETE_PROFILE ? (
        // Profile completion page not yet implemented in new web app
        // For now, redirect to projects listing
        <LinkButton color="primary" size="lg" to="/participants/settings">
          <Trans>Complete profile</Trans>
        </LinkButton>
      ) : (
        <LinkButton color="primary" size="lg" to="/participants/projects">
          <Trans>Browse other projects</Trans>
        </LinkButton>
      )}
    </div>
  );
}
